import Icon from "../../../../common/components/Icon/Icon";
import InventoryItem from "../../../../utils/game/mechanics/InventoryItem";
import { InventoryItemContainerProps } from "./InventoryItemContainer";
import InventoryItemIcon from "../InventoryItemIcon/InventoryItemIcon";
import FlexColumn from "../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import TextField from "../../../../common/components/display/TextField/TextField";
import {
    InventoryItemCategory,
    InventoryItemCategoryUtils,
} from "../../../../utils/sys/InventoryItemCategory";
import LabeledTextField from "../../../../common/components/display/LabeledTextField/LabeledTextField";

import currencyIcon from "../../../../assets/images/common/svg/currency.svg";
import weightIcon from "../../../../assets/images/common/svg/weight.svg";
import {ReactElement, useMemo} from "react";

import "./style/InventoryItemDetails_style.css";
import { DamageType } from "../../../../utils/game/mechanics/DamageType";
import DamageTypeUtils from "../../../../utils/game/mechanics/DamageType";

type Props = {
    item: InventoryItem | null;
    unidentified?: boolean;
    className?: string;
};

const hiddenDamageTypes = [DamageType.PHYSICAL, DamageType.MAGIC];

export default function InventoryItemDetailsPanel({
    item,
    unidentified,
    className,
}: Props): ReactElement {
    const dmgTypesString = useMemo(() => {
        if(item?.type === InventoryItemCategory.WEAPON) {
            return item.damageTypes.filter(dmgType => !hiddenDamageTypes.includes(dmgType)).map(dmgType => DamageTypeUtils.getLabel(dmgType)).join(" | ");
        }

        return null;
    }, [item?.damageTypes, item?.type]);

    return (
        <FlexColumn
            className={`inventory-item-details-container${
                className == null ? `` : ` ${className}`
            }`}
        >
            {item != null && (
                <FlexColumn gap={4}>
                    <FlexRow>
                        <InventoryItemIcon
                            bgSrc={item.bgSrc}
                            bgColor={item.bgColor}
                            iconSrc={item.iconSrc}
                            iconColor={item.iconColor}
                            className="inventory-item-details-item-icon"
                        />
                        <FlexColumn>
                            <TextField type="bold">
                                {unidentified
                                    ? item.unidentifiedTitle
                                    : item.title}
                            </TextField>
                            <TextField>{`${item.typeString}${
                                item.type === InventoryItemCategory.GENERIC
                                    ? ``
                                    : ` - ${item.subTypeString}`
                            }`}</TextField>
                            <FlexRow>
                                <Icon src={currencyIcon} size={16} />
                                <TextField>{item.value}</TextField>
                                <Icon src={weightIcon} size={16} />
                                <TextField>{item.weight}</TextField>
                            </FlexRow>
                        </FlexColumn>
                    </FlexRow>
                    {item.type === InventoryItemCategory.ACCESSORY && (
                        <FlexColumn></FlexColumn>
                    )}
                    {item.type === InventoryItemCategory.AMMUNITION && (
                        <FlexColumn>
                            <FlexRow>
                                <LabeledTextField label="Dmg">
                                    {item.dmg}
                                </LabeledTextField>
                                <LabeledTextField label="M.Dmg">
                                    {item.mdmg}
                                </LabeledTextField>
                            </FlexRow>
                        </FlexColumn>
                    )}
                    {item.type === InventoryItemCategory.ARMOR && (
                        <FlexColumn>
                            <FlexRow>
                                <LabeledTextField label="Def">
                                    {item.def}
                                </LabeledTextField>
                                <LabeledTextField label="M.Def">
                                    {item.mdef}
                                </LabeledTextField>
                            </FlexRow>
                        </FlexColumn>
                    )}
                    {item.type === InventoryItemCategory.COMPONENT && (
                        <FlexColumn></FlexColumn>
                    )}
                    {item.type === InventoryItemCategory.CONSUMABLE && (
                        <FlexColumn></FlexColumn>
                    )}
                    {item.type === InventoryItemCategory.WEAPON && (
                        <FlexColumn>
                            <FlexRow>
                                <LabeledTextField label="Dmg">
                                    {item.dmg}
                                </LabeledTextField>
                                <LabeledTextField label="M.Dmg">
                                    {item.mdmg}
                                </LabeledTextField>
                            </FlexRow>
                            {dmgTypesString != null && (
                                <FlexRow>
                                    <span>{dmgTypesString}</span>
                                </FlexRow>
                            )}
                            <FlexRow>
                                <LabeledTextField label="Pow">
                                    {item.pow}
                                </LabeledTextField>
                                <LabeledTextField label="M.Pow">
                                    {item.mpow}
                                </LabeledTextField>
                            </FlexRow>
                            <FlexRow>
                                <LabeledTextField label="Rng">
                                    {item.rng}
                                </LabeledTextField>
                                <LabeledTextField label="M.Rng">
                                    {item.mrng}
                                </LabeledTextField>
                            </FlexRow>
                        </FlexColumn>
                    )}
                    <TextField numberOfLines={10}>
                        {unidentified
                            ? item.unidentifiedDescription
                            : item.description}
                    </TextField>
                </FlexColumn>
            )}
        </FlexColumn>
    );
}
